// Administration › Finance — Casey's management view of the money.
//
// MRR, the monthly P&L, cash, vendor spend and the irregularity findings,
// computed by squish-backend (weekly on Mondays, monthly on the 3rd) and served
// through squish-crm-backend's /api/finance/* routes. Design and decisions:
// squish-docs architecture/finance-intelligence.md ("CRM Finance page").
//
// It is "management view, not the books": the accountant's figures win any
// disagreement, and the page says so in its subtitle.
//
// Who sees it: admin AND whoami's `finance` flag (App.jsx canSeePage). The
// backend enforces the same rule with an email allowlist; a 403 from ANY call
// here replaces the whole page with one sentence (lib/finance isRestricted).
//
// One card per noun — MRR, Findings, P&L, Vendors, Cash — each with its own
// loading and error state, and each behind its own error boundary, so one card
// that fails (a slow route, a shape the page did not expect) never blanks the
// others. Every amount is integer cents; the wording and arithmetic live in
// lib/finance.js. The views below are pure functions of their props
// (test/finance-page.test.js renders them); Finance() at the bottom owns the
// fetching.
//
// Reconstructed periods — weeks and months computed after the fact, before the
// snapshots existed — are marked wherever they are drawn: faded bars, hollow
// points, and a "reconstructed" tag on table rows.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BarChart, Bar, Cell, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
  fetchFinanceOverview, fetchFinanceReport, fetchFinanceTransactions, fetchFinanceVendors,
  setFinanceTransactionCategory, saveFinanceVendorRule, actOnFinanceFinding,
} from '../api';
import {
  money, pct, runwayText, monthLabel, dayLabel, shortDate, stampLabel,
  FAMILIES, familyLabel, familyColor, underFloor, categoryLabel, ruleLabel, sortFindings,
  canMakeRule, ruleForCounterparty, changeAt, MOVEMENTS, movementCents,
  isRestricted, RESTRICTED_MESSAGE,
} from '../lib/finance';

const BASIS = 'management view, not the books';
const RECON_TITLE = 'Reconstructed: computed after the fact from stored data, not snapshotted at the time';

// ─── Small pieces ────────────────────────────────────────────────────────────

function Tag({ tone, title, children }) {
  return <span className={`fin-tag${tone ? ` fin-tag-${tone}` : ''}`} title={title}>{children}</span>;
}

const ReconTag = ({ on }) => (on ? <Tag title={RECON_TITLE}>reconstructed</Tag> : null);

function Kpi({ label, value, tone, sub, subTone, highlight, title }) {
  return (
    <div className={`kpi${highlight ? ' kpi-hi' : ''}`} title={title || undefined}>
      <div className="kpi-label">{label}</div>
      <div className={`kpi-value${tone ? ` ${tone}` : ''}`}>{value}</div>
      <div className={`kpi-sub${subTone ? ` ${subTone}` : ''}`}>{sub || ' '}</div>
    </div>
  );
}

function Loading() {
  return (
    <div className="loading-state" style={{ height: 120 }}>
      <span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" />
    </div>
  );
}

function CardError({ error, onRetry, compact }) {
  return (
    <div className="fin-error" style={{ padding: compact ? '6px 14px' : '16px 14px' }}>
      ⚠ {error?.message || 'Could not load'}
      {onRetry && <button className="fin-btn" style={{ marginLeft: 10 }} onClick={onRetry}>retry</button>}
    </div>
  );
}

// A card: its header, then its body once the data it needs is here. The body
// is a function so nothing in it runs against data that has not arrived.
function Card({ title, count, meta, slot, onRetry, children }) {
  const data = slot?.data;
  return (
    <section className="table-card fin-card">
      <div className="table-header">
        <span className="table-title">
          {title}
          {count != null && <span className="table-count" style={{ marginLeft: 8 }}>{count}</span>}
        </span>
        {meta && <span className="fin-muted">{meta}</span>}
      </div>
      {data == null
        ? (slot?.error ? <CardError error={slot.error} onRetry={onRetry} /> : <Loading />)
        : <>
            {slot.error && <CardError error={slot.error} onRetry={onRetry} compact />}
            {children(data)}
          </>}
    </section>
  );
}

// One card that throws while drawing shows its own error; the rest of the page
// stays. `resetKey` changes with the data, so a refresh gets a fresh attempt.
class CardBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidUpdate(prev) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <section className="table-card fin-card">
        <div className="table-header"><span className="table-title">{this.props.name}</span></div>
        <div className="fin-error" style={{ padding: '16px 14px' }}>⚠ This card could not be drawn: {String(this.state.error?.message || this.state.error)}</div>
      </section>
    );
  }
}

function Line2({ label, value, total, tone, title, children }) {
  return (
    <div className={`fin-line${total ? ' fin-line-total' : ''}`} title={title}>
      <span>{label}{children}</span>
      <span className={tone ? `fin-${tone}` : undefined}>{value}</span>
    </div>
  );
}

const toneOf = (cents) => (cents == null ? undefined : cents < 0 ? 'bad' : undefined);
const pts = (v) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(1)} pts`;

// ─── 403 ─────────────────────────────────────────────────────────────────────

export function FinanceRestricted() {
  return <div className="fin-restricted">{RESTRICTED_MESSAGE}</div>;
}

// ─── Headline strip ──────────────────────────────────────────────────────────

export function HeadlineStrip({ slot, onRetry }) {
  const o = slot?.data;
  if (!o) {
    return slot?.error
      ? <div className="table-card fin-card"><CardError error={slot.error} onRetry={onRetry} /></div>
      : <div className="kpi-strip" style={{ marginBottom: 0 }}>{['MRR', 'Revenue MTD', 'Gross margin', 'Cash', 'Runway', 'Open findings'].map(l => <Kpi key={l} label={l} value="…" />)}</div>;
  }
  const weekly = o.weeklyTrend || [];
  const monthly = o.monthlyTrend || [];
  const lw = o.latestWeekly || null;
  const lm = o.latestMonthly || null;
  const lastMonth = monthly[monthly.length - 1] || null;

  const mrr = lw?.mrr?.mrrCents ?? weekly[weekly.length - 1]?.mrrCents ?? null;
  const arr = lw?.mrr?.arrCents ?? (mrr != null ? mrr * 12 : null);
  const mrrDelta = changeAt(weekly, lw?.key ?? null, 'mrrCents');

  const mtd = o.live?.monthToDate || null;

  const gmPct = lm?.pnl?.grossMarginPct ?? lastMonth?.grossMarginPct ?? null;
  const gmKey = lm?.key ?? lastMonth?.key ?? null;
  const gmDelta = changeAt(monthly, gmKey, 'grossMarginPct');
  const floor = lm?.pnl?.floorPct ?? 10;

  const cash = o.live?.cash || null;
  const weekCash = weekly[weekly.length - 1]?.operatingCashCents ?? lw?.cash?.netCents ?? null;

  // Runway is the latest month's. null from the backend = not burning.
  const runwaySrc = lm?.cash && 'runwayMonths' in lm.cash ? lm.cash
    : lastMonth && 'runwayMonths' in lastMonth ? lastMonth : null;
  const runway = runwaySrc ? runwaySrc.runwayMonths : undefined;
  const burn = lm?.cash?.operating?.burnCents ?? null;

  const f = o.findings || {};
  const opened = lw?.findings?.opened || 0;
  const errors = o.live?.errors || [];

  return (
    <div>
      <div className="kpi-strip" style={{ marginBottom: 0 }}>
        <Kpi
          label="MRR" value={money(mrr, { compact: true })} tone="accent" highlight
          sub={`ARR ${money(arr, { compact: true })}${mrrDelta != null ? ` · ${money(mrrDelta, { compact: true, sign: true })} w/w` : ''}`}
          subTone={mrrDelta > 0 ? 'good' : mrrDelta < 0 ? 'warn' : undefined}
          title={lw?.mrr ? `MRR as of ${dayLabel(lw.key)} (${lw.mrr.basis || 'list price'}): ${money(mrr)}. ARR ${money(arr)}.` : undefined}
        />
        <Kpi
          label="Revenue MTD" value={mtd ? money(mtd.revenueCents, { compact: true }) : '—'} tone="green"
          sub={mtd ? `${mtd.charges ?? 0} charge${mtd.charges === 1 ? '' : 's'} · ${monthLabel(mtd.month, { year: false })}` : 'live figure unavailable'}
          subTone={mtd ? undefined : 'warn'}
          title={mtd ? `Net of refunds. Gross margin so far ${money(mtd.grossMarginCents)}.` : undefined}
        />
        <Kpi
          label="Gross margin" value={pct(gmPct)}
          tone={gmPct != null && gmPct < floor ? 'yellow' : undefined}
          sub={gmKey ? `${monthLabel(gmKey, { year: false })}${gmDelta != null ? ` · ${pts(gmDelta)} m/m` : ''}` : 'no month yet'}
        />
        <Kpi
          label="Cash" value={cash ? money(cash.depositCents, { compact: true }) : '—'}
          sub={cash
            ? `card owed ${money(cash.cardOwedCents, { compact: true })}${weekCash != null ? ` · ${money(weekCash, { compact: true, sign: true })} wk` : ''}`
            : 'live balance unavailable'}
          subTone={cash ? undefined : 'warn'}
          title={cash ? `Deposits ${money(cash.depositCents)} · card owed ${money(cash.cardOwedCents)}${weekCash != null ? ` · last week's operating cash ${money(weekCash, { sign: true })}` : ''}` : undefined}
        />
        <Kpi
          label="Runway" value={runwayText(runway)}
          tone={runway === null ? 'green' : runway != null && runway < 6 ? 'pink' : undefined}
          sub={burn != null && burn > 0 ? `burn ${money(burn, { compact: true })}/mo · ${monthLabel(gmKey, { year: false })}` : (gmKey ? monthLabel(gmKey, { year: false }) : ' ')}
        />
        <Kpi
          label="Open findings" value={f.openCount ?? '—'}
          tone={f.high > 0 ? 'pink' : undefined}
          sub={`${f.high ?? 0} high${opened > 0 ? ` · +${opened} this wk` : ''}`}
          subTone={f.high > 0 ? 'warn' : undefined}
        />
      </div>
      {errors.length > 0 && (
        <div className="kpi-scope kpi-scope-warn" style={{ marginTop: 6, marginBottom: 0, textTransform: 'none', letterSpacing: 0 }}>
          Live figures partial — {errors.join(' · ')}
        </div>
      )}
    </div>
  );
}

// ─── MRR ─────────────────────────────────────────────────────────────────────

function MrrTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="fin-tip">
      <div style={{ color: 'var(--text2)', marginBottom: 4 }}>
        Week of {dayLabel(row.key)} · <span style={{ color: 'var(--text)', fontWeight: 700 }}>{money(row.total)}</span>
      </div>
      {row.reconstructed && <div style={{ color: 'var(--text3)', marginBottom: 4 }}>reconstructed</div>}
      {[...payload].reverse().filter(p => p.value > 0).map(p => (
        <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, color: 'var(--text2)' }}>
          <span><span className="fin-swatch" style={{ background: familyColor(p.dataKey) }} />{familyLabel(p.dataKey)}</span>
          <span style={{ color: 'var(--text)' }}>{money(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

function MovementCell({ movements, m }) {
  if (!movements) return <td className="fin-num fin-muted">—</td>;
  const cents = movementCents(movements, m);
  const count = movements[`${m.key}Count`];
  return (
    <td className="fin-num">
      {money(cents, { sign: true })}
      {count > 0 && <span className="fin-muted" style={{ marginLeft: 5 }}>×{count}</span>}
    </td>
  );
}

export function MrrCard({ slot, onRetry }) {
  return (
    <Card title="MRR" slot={slot} onRetry={onRetry}
      meta={slot?.data?.latestWeekly?.mrr ? `ARR ${money(slot.data.latestWeekly.mrr.arrCents, { compact: true })}` : null}>
      {(o) => {
        const weekly = o.weeklyTrend || [];
        const monthly = o.monthlyTrend || [];
        const lw = o.latestWeekly || null;
        const lm = o.latestMonthly || null;
        const active = FAMILIES.filter(fam => weekly.some(w => (w.byFamily?.[fam] || 0) > 0));
        const data = weekly.map(w => ({
          key: w.key, label: dayLabel(w.key), reconstructed: !!w.reconstructed, total: w.mrrCents,
          ...Object.fromEntries(FAMILIES.map(fam => [fam, w.byFamily?.[fam] || 0])),
        }));
        const anyRecon = data.some(d => d.reconstructed);
        const latestFamilies = weekly[weekly.length - 1]?.byFamily || {};
        const planRows = monthly.filter(m => m.planCents != null).slice(-6).reverse();
        return (
          <>
            <div className="fin-section">
              <div className="fin-section-title">
                <span>Weekly MRR by family</span>
                {anyRecon && <span title={RECON_TITLE} style={{ textTransform: 'none', letterSpacing: 0 }}>faded = reconstructed</span>}
              </div>
              {data.length === 0 || active.length === 0 ? (
                <div className="fin-empty">No weekly report yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height={190}>
                  <BarChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="#2a2a38" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: '#5a5a72', fontSize: 10, fontFamily: 'DM Mono' }} axisLine={false} tickLine={false} />
                    <YAxis width={48} tick={{ fill: '#5a5a72', fontSize: 10, fontFamily: 'DM Mono' }} axisLine={false} tickLine={false} tickFormatter={(v) => money(v, { compact: true })} />
                    <Tooltip content={<MrrTooltip />} cursor={{ fill: 'rgba(124,109,250,0.08)' }} />
                    {active.map((fam, i) => (
                      // isAnimationActive={false}: see Sales.jsx — recharts
                      // freezes animated bars at their first width in a grid.
                      <Bar key={fam} dataKey={fam} stackId="mrr" fill={familyColor(fam)} stroke="var(--bg2)" strokeWidth={1}
                        maxBarSize={36} isAnimationActive={false}
                        radius={i === active.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}>
                        {data.map(d => <Cell key={d.key} fillOpacity={d.reconstructed ? 0.4 : 1} />)}
                      </Bar>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
              {active.length > 0 && (
                // The legend doubles as the latest week's figures, so no
                // family's number depends on reading a colour.
                <div className="fin-legend" style={{ marginTop: 6 }}>
                  {active.map(fam => (
                    <span key={fam}><span className="fin-swatch" style={{ background: familyColor(fam) }} />{familyLabel(fam)} {money(latestFamilies[fam] || 0)}</span>
                  ))}
                </div>
              )}
            </div>

            <div className="fin-section" style={{ padding: 0 }}>
              <table>
                <thead>
                  <tr>
                    <th>Movements</th>
                    <th className="fin-num">{lw ? `Wk of ${dayLabel(lw.key)}` : 'Week'}</th>
                    <th className="fin-num">{lm ? monthLabel(lm.key) : 'Month'}</th>
                  </tr>
                </thead>
                <tbody>
                  {MOVEMENTS.map(m => (
                    <tr key={m.key}>
                      <td>{m.label}</td>
                      <MovementCell movements={lw?.movements} m={m} />
                      <MovementCell movements={lm?.movements} m={m} />
                    </tr>
                  ))}
                  <tr>
                    <td style={{ color: 'var(--text)', fontWeight: 700 }}>Net</td>
                    <td className="fin-num" style={{ color: 'var(--text)', fontWeight: 700 }}>{lw?.movements ? money(lw.movements.netCents, { sign: true }) : '—'}</td>
                    <td className="fin-num" style={{ color: 'var(--text)', fontWeight: 700 }}>{lm?.movements ? money(lm.movements.netCents, { sign: true }) : '—'}</td>
                  </tr>
                </tbody>
              </table>
              {(!lw?.movements || !lm?.movements) && (
                <div className="fin-muted" style={{ padding: '4px 14px 8px' }}>— = no earlier snapshot to compare against</div>
              )}
            </div>

            <div className="fin-section" style={{ padding: 0 }}>
              {planRows.length === 0 ? (
                <div className="fin-empty">No plan figures for these months.</div>
              ) : (
                <table>
                  <thead><tr><th>Plan vs actual</th><th className="fin-num">MRR</th><th className="fin-num">Plan</th><th className="fin-num">Gap</th><th className="fin-num">Of plan</th></tr></thead>
                  <tbody>
                    {planRows.map(m => {
                      const gap = (m.mrrCents ?? 0) - m.planCents;
                      return (
                        <tr key={m.key}>
                          <td>{monthLabel(m.key)}<ReconTag on={m.reconstructed} /></td>
                          <td className="fin-num">{money(m.mrrCents)}</td>
                          <td className="fin-num">{money(m.planCents)}</td>
                          <td className={`fin-num${gap < 0 ? ' fin-bad' : ''}`}>{money(gap, { sign: true })}</td>
                          <td className="fin-num">{m.planCents > 0 && m.mrrCents != null ? `${Math.round((m.mrrCents / m.planCents) * 100)}%` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        );
      }}
    </Card>
  );
}

// ─── Findings ────────────────────────────────────────────────────────────────

const SEVERITY_BADGE = { high: 'badge-red', medium: 'badge-yellow', low: 'badge-gray' };

function detailText(detail) {
  if (!detail || typeof detail !== 'object') return '';
  return Object.entries(detail)
    .filter(([, v]) => v != null && typeof v !== 'object')
    .slice(0, 4)
    .map(([k, v]) => {
      const words = k.replace(/(Cents|Pct)$/, '').replace(/([A-Z])/g, ' $1').toLowerCase().trim();
      const val = /Cents$/.test(k) ? money(v) : /Pct$/.test(k) ? pct(v) : String(v);
      return `${words} ${val}`;
    })
    .join(' · ');
}

function FindingRow({ finding: f, state, onAct }) {
  const [note, setNote] = useState('');
  const busy = state?.busy;
  const act = async (action) => {
    const ok = await onAct?.(f.id, action, note.trim());
    if (ok) setNote('');
  };
  const ref = f.ref || {};
  const context = [ref.vendorKey, ref.family && familyLabel(ref.family), ref.period].filter(Boolean).join(' · ');
  const ids = [ref.mercuryTxnId, ...(ref.mercuryTxnIds || []), ref.stripePayoutId, ref.disputeId].filter(Boolean).join(', ');
  const detail = detailText(f.detail);
  return (
    <div className="fin-finding">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span className={`badge ${SEVERITY_BADGE[f.severity] || 'badge-gray'}`} style={{ padding: '1px 6px', fontSize: 10 }}>{f.severity || '—'}</span>
        <span style={{ color: 'var(--text)', fontWeight: 600, fontSize: 13, flex: 1, minWidth: 0 }} title={f.rule}>{ruleLabel(f.rule)}</span>
        {f.amountCents != null && <span className="fin-num" style={{ color: 'var(--text)', fontSize: 12 }}>{money(f.amountCents)}</span>}
      </div>
      <div className="fin-muted" style={{ marginTop: 2 }} title={ids || undefined}>
        since {shortDate(f.firstSeenAt)}{context ? ` · ${context}` : ''}
        {f.status === 'acknowledged' && <Tag tone="warn">acknowledged</Tag>}
        {f.recheck === 'inconclusive' && <Tag title="The last re-check could not confirm either way">recheck inconclusive</Tag>}
        <ReconTag on={f.reconstructed} />
      </div>
      {detail && <div className="fin-muted" style={{ marginTop: 2 }}>{detail}</div>}
      {(f.notes || []).length > 0 && (
        <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {f.notes.map((n, i) => (
            <div key={i} className="fin-note">
              <span className="fin-muted">{shortDate(n.at)} · {n.by || '—'} · {n.action}</span>
              {n.text ? <span> — {n.text}</span> : null}
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        <input
          className="fin-input" data-write style={{ flex: 1, minWidth: 0 }}
          placeholder="Note (optional)" maxLength={500} value={note}
          onChange={e => setNote(e.target.value)} disabled={!!busy}
          aria-label={`Note for ${ruleLabel(f.rule)}`}
        />
        {f.status !== 'acknowledged' && (
          <button className="fin-btn" data-write disabled={!!busy} onClick={() => act('acknowledge')}>
            {busy === 'acknowledge' ? 'Saving…' : 'Acknowledge'}
          </button>
        )}
        <button className="fin-btn fin-btn-primary" data-write disabled={!!busy} onClick={() => act('resolve')}>
          {busy === 'resolve' ? 'Saving…' : 'Resolve'}
        </button>
      </div>
      {state?.error && <div className="fin-error" style={{ marginTop: 4 }}>⚠ {state.error.message}</div>}
    </div>
  );
}

export function FindingsCard({ slot, onRetry, acting, onAct }) {
  const f = slot?.data?.findings;
  return (
    <Card title="Findings" slot={slot} onRetry={onRetry} count={f?.openCount ?? null}
      meta={f ? `${f.high ?? 0} high` : null}>
      {(o) => {
        const list = sortFindings(o.findings?.open);
        if (list.length === 0) return <div className="fin-empty">No open findings.</div>;
        return (
          <div className="fin-findings">
            {list.map(item => <FindingRow key={item.id} finding={item} state={acting?.[item.id]} onAct={onAct} />)}
          </div>
        );
      }}
    </Card>
  );
}

// ─── P&L ─────────────────────────────────────────────────────────────────────

// The full statement for one month: GET /api/finance/report/monthly/:key, or
// overview.latestMonthly for the latest. Every amount is shown as its effect
// on the operating result, so the column adds up as read.
export function PnlStatement({ report }) {
  const p = report?.pnl || {};
  const cash = report?.cash || {};
  const tie = report?.tieOut || null;
  const floor = Number.isFinite(Number(p.floorPct)) ? Number(p.floorPct) : 10;
  const families = Object.entries(p.byFamily || {})
    .sort((a, b) => (b[1]?.revenueCents || 0) - (a[1]?.revenueCents || 0));
  const opex = Object.entries(p.opexByCategory || {}).sort((a, b) => (b[1] || 0) - (a[1] || 0));
  const neg = (c) => (c == null ? null : -c);

  return (
    <div className="fin-stmt">
      <div className="fin-section-title" style={{ padding: '10px 14px 0' }}>
        <span>
          {monthLabel(report?.key, { long: true })}
          <ReconTag on={report?.reconstructed} />
          {report?.closed === false && <Tag title="The month-end audit has not closed this month yet">not closed</Tag>}
          {report?.late && <Tag title="Computed later than its scheduled run">late</Tag>}
        </span>
        <span style={{ textTransform: 'none', letterSpacing: 0 }}>{p.basis || BASIS}</span>
      </div>
      <div className="fin-stmt-cols">
        <div className="fin-section" style={{ borderTop: 'none' }}>
          <Line2 label="Gross charges" value={money(p.grossCents)} />
          <Line2 label="Refunds" value={money(neg(p.refundCents))} />
          <Line2 label="Revenue" value={money(p.revenueCents)} total />
          <Line2 label="COGS" value={money(neg(p.cogsCents))} />
          <Line2 label="Stripe fees" value={money(neg(p.feeCents))}
            title={`Charge fees ${money(p.chargeFeeCents)} + other Stripe fees ${money(p.otherStripeFeeCents)}`} />
          <Line2 label="Gross margin" value={`${money(p.grossMarginCents)} · ${pct(p.grossMarginPct)}`} total tone={toneOf(p.grossMarginCents)} />
          <Line2 label="Opex" value={money(neg(p.opexCents))} />
          {p.uncategorisedOpexCents > 0 && (
            <Line2 label="of which uncategorised" value={money(neg(p.uncategorisedOpexCents))} tone="warn" />
          )}
          <Line2 label="COGS true-up" value={money(neg(p.cogsTrueUpCents), { sign: true })}
            title={`Supplier cash ${money(p.supplierCashCents)} − ledger COGS = ${money(p.cogsTrueUpCents)}; shown as its effect on the result`} />
          <Line2 label="Other income" value={money(p.otherIncomeCents, { sign: true })} />
          <Line2 label="Operating result" value={money(p.operatingResultCents)} total tone={toneOf(p.operatingResultCents)} />
        </div>

        <div className="fin-section" style={{ borderTop: 'none' }}>
          <div className="fin-section-title"><span>Opex by category</span></div>
          {opex.length === 0
            ? <div className="fin-muted">No opex this month.</div>
            : opex.map(([cat, cents]) => <Line2 key={cat} label={categoryLabel(cat)} value={money(neg(cents))} />)}

          <div className="fin-section-title" style={{ marginTop: 10 }}><span>CRM tie-out</span></div>
          {!tie ? <div className="fin-muted">not run</div>
            : tie.status === 'ok' ? (
              <div className="fin-line">
                <span>
                  CRM {money(tie.crmNetCents)} · Stripe {money(tie.stripeNetCents)}{tie.orders != null ? ` · ${tie.orders} orders` : ''}
                  {tie.diffCents ? <Tag tone="bad">differs</Tag> : <Tag tone="good">agrees</Tag>}
                </span>
                <span className={tie.diffCents ? 'fin-bad' : undefined}>{money(tie.diffCents, { sign: true })}</span>
              </div>
            ) : <div className="fin-muted">{tie.status === 'maintenance' ? 'skipped: CRM in maintenance' : tie.status === 'error' ? 'failed to run' : 'not run'}</div>}

          <div className="fin-section-title" style={{ marginTop: 10 }}><span>Cash</span></div>
          <Line2 label="Opening deposits" value={money(cash.openingDepositCents)} />
          <Line2 label="Closing deposits" value={money(cash.closingDepositCents)} />
          {cash.operating && <Line2 label="Operating burn" value={money(cash.operating.burnCents)}
            title={`In ${money(cash.operating.inCents)} · out ${money(cash.operating.outCents)} · net ${money(cash.operating.netCents, { sign: true })}`} />}
          {(cash.owner?.inCents > 0 || cash.owner?.outCents > 0) && (
            <Line2 label="Owner in / out" value={`${money(cash.owner.inCents)} / ${money(cash.owner.outCents)}`} />
          )}
          <Line2 label="Runway" value={runwayText(cash.runwayMonths)} />
        </div>
      </div>

      <div className="fin-section" style={{ padding: 0 }}>
        <table>
          <thead>
            <tr><th>Margin by family</th><th className="fin-num">Revenue</th><th className="fin-num">COGS</th><th className="fin-num">Fees</th><th className="fin-num">Margin</th><th className="fin-num">Margin %</th></tr>
          </thead>
          <tbody>
            {families.length === 0 && <tr><td colSpan={6} className="fin-muted">No sales this month.</td></tr>}
            {families.map(([fam, v]) => {
              const flagged = underFloor(v, floor);
              return (
                <tr key={fam}>
                  <td>
                    {familyLabel(fam)}
                    {flagged && <Tag tone="bad" title={`Realised margin ${pct(v?.marginPct)} is under the ${floor}% floor`}>under {floor}% floor</Tag>}
                  </td>
                  <td className="fin-num">{money(v?.revenueCents)}</td>
                  <td className="fin-num">{money(neg(v?.cogsCents))}</td>
                  <td className="fin-num">{money(neg(v?.feeCents))}</td>
                  <td className="fin-num">{money(v?.marginCents)}</td>
                  <td className={`fin-num${flagged ? ' fin-bad' : ''}`}>{pct(v?.marginPct)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function PnlCard({ slot, onRetry, selectedKey, statement, onSelectMonth }) {
  return (
    <Card title="P&L" slot={slot} onRetry={onRetry} meta={`last 12 months · ${BASIS}`}>
      {(o) => {
        const lm = o.latestMonthly || null;
        const rows = (o.monthlyTrend || []).slice(-12).reverse();
        // monthlyTrend carries revenue and gross margin but not the COGS / fee
        // split; the latest month's report has it, and a backend that adds
        // cogsCents / feeCents to the trend rows gets them shown everywhere.
        const split = (m) => {
          const fromLatest = lm && lm.key === m.key ? lm.pnl : null;
          const cogs = m.cogsCents ?? fromLatest?.cogsCents;
          const fee = m.feeCents ?? fromLatest?.feeCents;
          return cogs != null && fee != null ? { cogs, fee } : null;
        };
        const floor = lm?.pnl?.floorPct ?? 10;
        return (
          <>
            <div className="fin-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Month</th><th className="fin-num">Revenue</th><th className="fin-num">COGS</th><th className="fin-num">Stripe fees</th>
                    <th className="fin-num">Gross margin</th><th className="fin-num">GM %</th><th className="fin-num">Opex</th><th className="fin-num">Operating result</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={8} className="fin-muted">No monthly report yet.</td></tr>}
                  {rows.map(m => {
                    const s = split(m);
                    const combined = m.revenueCents != null && m.grossMarginCents != null ? m.revenueCents - m.grossMarginCents : null;
                    const selected = m.key === selectedKey;
                    return (
                      <tr
                        key={m.key}
                        className={`fin-row-pick${selected ? ' fin-row-active' : ''}`}
                        tabIndex={0}
                        aria-selected={selected}
                        onClick={() => onSelectMonth?.(m.key)}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectMonth?.(m.key); } }}
                        title={`${monthLabel(m.key, { long: true })}: open the full statement`}
                      >
                        <td style={{ whiteSpace: 'nowrap', color: 'var(--text)' }}>
                          {monthLabel(m.key)}
                          <ReconTag on={m.reconstructed} />
                          {m.closed === false && <Tag title="The month-end audit has not closed this month yet">open</Tag>}
                        </td>
                        <td className="fin-num">{money(m.revenueCents)}</td>
                        {s ? (
                          <>
                            <td className="fin-num">{money(-s.cogs)}</td>
                            <td className="fin-num">{money(-s.fee)}</td>
                          </>
                        ) : (
                          <td className="fin-num" colSpan={2} title="COGS and Stripe fees together; the month's statement splits them">
                            {money(combined == null ? null : -combined)} <span className="fin-muted">COGS + fees</span>
                          </td>
                        )}
                        <td className={`fin-num${m.grossMarginCents < 0 ? ' fin-bad' : ''}`}>{money(m.grossMarginCents)}</td>
                        <td className={`fin-num${m.grossMarginPct != null && m.grossMarginPct < floor ? ' fin-warn' : ''}`}>{pct(m.grossMarginPct)}</td>
                        <td className="fin-num">{money(m.opexCents == null ? null : -m.opexCents)}</td>
                        <td className={`fin-num${m.operatingResultCents < 0 ? ' fin-bad' : ''}`} style={{ fontWeight: 700 }}>{money(m.operatingResultCents)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ borderTop: '1px solid var(--border)' }}>
              {!selectedKey ? <div className="fin-empty">Pick a month for its full statement.</div>
                : statement?.data ? <PnlStatement report={statement.data} />
                : statement?.error ? (
                  <div className="fin-error" style={{ padding: '14px' }}>
                    ⚠ {statement.error.status === 404 ? `No monthly report for ${monthLabel(selectedKey)}.` : statement.error.message}
                  </div>
                ) : <Loading />}
            </div>
          </>
        );
      }}
    </Card>
  );
}

// ─── Cash ────────────────────────────────────────────────────────────────────

function CashDot({ cx, cy, payload }) {
  if (cx == null || cy == null) return null;
  return <circle cx={cx} cy={cy} r={4} fill={payload?.reconstructed ? 'var(--bg2)' : 'var(--accent)'} stroke="var(--accent)" strokeWidth={1.5} />;
}

function CashTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  return (
    <div className="fin-tip">
      <div style={{ color: 'var(--text2)' }}>{monthLabel(row.key)} close · <span style={{ color: 'var(--text)', fontWeight: 700 }}>{money(row.cash)}</span></div>
      {row.reconstructed && <div style={{ color: 'var(--text3)' }}>reconstructed</div>}
    </div>
  );
}

export function CashCard({ slot, onRetry }) {
  return (
    <Card title="Cash" slot={slot} onRetry={onRetry} meta={slot?.data?.asOf ? `as of ${stampLabel(slot.data.asOf)}` : null}>
      {(o) => {
        const live = o.live?.cash || null;
        const lm = o.latestMonthly || null;
        const lw = o.latestWeekly || null;
        const monthly = o.monthlyTrend || [];
        const data = monthly.filter(m => m.closingCashCents != null)
          .map(m => ({ key: m.key, label: monthLabel(m.key, { year: false }), cash: m.closingCashCents, reconstructed: !!m.reconstructed }));
        const burn = lm?.cash?.operating?.burnCents;
        const trailing = lm?.cash?.trailingBurnCents || [];
        const recent = monthly.slice(-6).reverse();
        return (
          <>
            <div className="fin-section fin-mini-grid">
              <div><div className="kpi-label">Deposits · live</div><div className="fin-mini-value">{live ? money(live.depositCents) : '—'}</div></div>
              <div><div className="kpi-label">Card owed · live</div><div className="fin-mini-value">{live ? money(live.cardOwedCents) : '—'}</div></div>
              <div><div className="kpi-label">Burn · {lm ? monthLabel(lm.key, { year: false }) : 'month'}</div><div className="fin-mini-value">{burn != null ? money(burn) : '—'}</div></div>
              <div><div className="kpi-label">Runway</div><div className="fin-mini-value">{runwayText(lm?.cash && 'runwayMonths' in lm.cash ? lm.cash.runwayMonths : undefined)}</div></div>
            </div>
            {trailing.length > 0 && (
              <div className="fin-muted" style={{ padding: '0 14px 8px' }} title="Net operating burn of the trailing months, newest first; runway = closing deposits ÷ their average">
                trailing burn {trailing.map(c => money(c, { compact: true })).join(' · ')}
              </div>
            )}
            {lw?.cash && (
              <div className="fin-muted" style={{ padding: '0 14px 8px' }}>
                week {dayLabel(lw.from)}–{dayLabel(lw.to)}: in {money(lw.cash.inCents)} · out {money(lw.cash.outCents)} · net {money(lw.cash.netCents, { sign: true })}
                {lw.cash.pendingRows > 0 && <Tag tone="warn">{lw.cash.pendingRows} pending</Tag>}
              </div>
            )}
            <div className="fin-section">
              <div className="fin-section-title"><span>Closing cash by month</span>{data.some(d => d.reconstructed) && <span style={{ textTransform: 'none', letterSpacing: 0 }}>hollow = reconstructed</span>}</div>
              {data.length === 0 ? <div className="fin-empty">No month closed yet.</div> : (
                <ResponsiveContainer width="100%" height={140}>
                  <LineChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="#2a2a38" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: '#5a5a72', fontSize: 10, fontFamily: 'DM Mono' }} axisLine={false} tickLine={false} />
                    <YAxis width={52} tick={{ fill: '#5a5a72', fontSize: 10, fontFamily: 'DM Mono' }} axisLine={false} tickLine={false} tickFormatter={(v) => money(v, { compact: true })} domain={['auto', 'auto']} />
                    <Tooltip content={<CashTooltip />} />
                    <Line type="linear" dataKey="cash" stroke="var(--accent)" strokeWidth={2} dot={<CashDot />} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
            {recent.length > 0 && (
              <div className="fin-section" style={{ padding: 0 }}>
                <table>
                  <thead><tr><th>Month</th><th className="fin-num">Closing cash</th><th className="fin-num">Runway</th></tr></thead>
                  <tbody>
                    {recent.map(m => (
                      <tr key={m.key}>
                        <td>{monthLabel(m.key)}<ReconTag on={m.reconstructed} /></td>
                        <td className="fin-num">{money(m.closingCashCents)}</td>
                        <td className="fin-num">{runwayText('runwayMonths' in m ? m.runwayMonths : undefined)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        );
      }}
    </Card>
  );
}

// ─── Vendors ─────────────────────────────────────────────────────────────────

export const counterpartyId = (row) => `${row?.direction || ''}|${row?.counterpartyName || ''}`;

function UncategorisedRow({ row, categories, busy, error, onAlways, onJustThis }) {
  const [category, setCategory] = useState('');
  const name = row.counterpartyName || '—';
  const ruleOk = canMakeRule(row.counterpartyName);
  const chosen = category ? categoryLabel(category) : 'a category';
  return (
    <tr>
      <td style={{ color: 'var(--text)', fontWeight: 600 }}>
        {name}
        <Tag>{row.direction === 'in' ? 'in' : 'out'}</Tag>
        {(row.kinds || []).length > 0 && <div className="fin-muted" style={{ fontWeight: 400 }}>{row.kinds.join(', ')}</div>}
        {error && <div className="fin-error" style={{ fontWeight: 400 }}>⚠ {error.message}</div>}
      </td>
      <td className="fin-num">{row.count ?? '—'}</td>
      <td className="fin-num">{money(row.totalCents)}</td>
      <td style={{ whiteSpace: 'nowrap' }}>
        <select className="fin-select" data-write value={category} disabled={busy} onChange={e => setCategory(e.target.value)} aria-label={`Category for ${name}`}>
          <option value="">Category…</option>
          {categories.map(c => <option key={c} value={c}>{categoryLabel(c)}</option>)}
        </select>
        <button
          className="fin-btn fin-btn-primary" data-write style={{ marginLeft: 6 }}
          disabled={!category || busy || !ruleOk}
          onClick={() => onAlways?.(row, category)}
          title={ruleOk
            ? `Make a rule: every ${row.direction === 'in' ? 'payment in from' : 'payment out to'} “${name}” is ${chosen}, past rows included`
            : 'This name has too few letters or digits to make a rule from; classify it one row at a time'}
        >Always</button>
        <button
          className="fin-btn" data-write style={{ marginLeft: 6 }}
          disabled={!category || busy || !row.sampleTxnId}
          onClick={() => onJustThis?.(row, category)}
          title={row.count > 1 ? `Classify one of the ${row.count} rows as ${chosen}; the rest stay in this queue` : `Classify this row as ${chosen}`}
        >Just this one</button>
      </td>
    </tr>
  );
}

export function VendorsCard({ slot, onRetry, classify, onAlways, onJustThis, selectedVendor, onSelectVendor, txns }) {
  return (
    <Card title="Vendors" slot={slot} onRetry={onRetry} count={slot?.data?.vendors?.length ?? null}
      meta={slot?.data?.rules ? `${slot.data.rules.length} rule${slot.data.rules.length === 1 ? '' : 's'}` : null}>
      {(v) => {
        const queue = v.uncategorised || [];
        const categories = v.categories || [];
        const months = (v.months || []).slice(-6);
        const vendors = [...(v.vendors || [])].sort((a, b) => Math.abs(b.totalCents || 0) - Math.abs(a.totalCents || 0));
        const picked = vendors.find(x => x.vendorKey === selectedVendor) || null;
        return (
          <>
            <div className="fin-section" style={{ padding: 0 }}>
              <div className="fin-section-title" style={{ padding: '10px 14px 0' }}>
                <span>Uncategorised · {queue.length}</span>
                <span style={{ textTransform: 'none', letterSpacing: 0 }}>Always = a rule that sticks</span>
              </div>
              {classify?.note && <div className="fin-ok fin-muted" style={{ padding: '4px 14px 0', color: 'var(--accent3)' }}>✓ {classify.note}</div>}
              {queue.length === 0 ? <div className="fin-empty">Nothing uncategorised.</div> : (
                <div className="fin-scroll">
                  <table>
                    <thead><tr><th>Counterparty</th><th className="fin-num">Rows</th><th className="fin-num">Total</th><th>Classify</th></tr></thead>
                    <tbody>
                      {queue.map(row => (
                        <UncategorisedRow
                          key={counterpartyId(row)} row={row} categories={categories}
                          busy={classify?.busy === counterpartyId(row)}
                          error={classify?.errorFor === counterpartyId(row) ? classify.error : null}
                          onAlways={onAlways} onJustThis={onJustThis}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="fin-section" style={{ padding: 0 }}>
              <div className="fin-section-title" style={{ padding: '10px 14px 0' }}>
                <span>Spend by vendor</span>
                <span style={{ textTransform: 'none', letterSpacing: 0 }}>last {months.length} months · total = {(v.months || []).length}</span>
              </div>
              {vendors.length === 0 ? <div className="fin-empty">No vendor spend yet.</div> : (
                <div className="fin-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Vendor</th>
                        {months.map(m => <th key={m} className="fin-num">{monthLabel(m, { year: false })}</th>)}
                        <th className="fin-num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendors.map(x => {
                        const selected = x.vendorKey === selectedVendor;
                        return (
                          <tr
                            key={x.vendorKey}
                            className={`fin-row-pick${selected ? ' fin-row-active' : ''}`}
                            tabIndex={0}
                            aria-selected={selected}
                            onClick={() => onSelectVendor?.(x.vendorKey)}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelectVendor?.(x.vendorKey); } }}
                            title={`${x.name}: show its transactions`}
                          >
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <span style={{ color: 'var(--text)', fontWeight: 600 }}>{x.name || x.vendorKey}</span>
                              <span className="fin-muted" style={{ marginLeft: 6 }}>{categoryLabel(x.category)}</span>
                            </td>
                            {months.map(m => <td key={m} className="fin-num">{x.byMonth?.[m] ? money(x.byMonth[m], { compact: true }) : <span className="fin-muted">·</span>}</td>)}
                            <td className="fin-num" style={{ color: 'var(--text)' }}>{money(x.totalCents, { compact: true })}<span className="fin-muted" style={{ marginLeft: 4 }}>×{x.count ?? 0}</span></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {selectedVendor && (
              <div className="fin-section" style={{ padding: 0 }}>
                <div className="fin-section-title" style={{ padding: '10px 14px 0' }}>
                  <span>{picked?.name || selectedVendor} · transactions</span>
                  <button className="fin-btn" onClick={() => onSelectVendor?.(selectedVendor)}>close</button>
                </div>
                {txns?.key !== selectedVendor || (!txns.data && !txns.error) ? <Loading />
                  : txns.error ? <CardError error={txns.error} compact />
                  : (txns.data.rows || []).length === 0 ? <div className="fin-empty">No transactions.</div>
                  : (
                    <div className="fin-scroll">
                      <table>
                        <thead><tr><th>Date</th><th>Counterparty</th><th className="fin-num">Amount</th><th>Category</th><th>From</th></tr></thead>
                        <tbody>
                          {txns.data.rows.map(t => (
                            <tr key={t.id}>
                              <td className="fin-muted" style={{ whiteSpace: 'nowrap' }}>{dayLabel(t.bookDate)}</td>
                              <td>{t.counterpartyName || '—'}{t.status && t.status !== 'sent' && <Tag>{t.status}</Tag>}</td>
                              <td className={`fin-num${t.amountCents > 0 ? ' fin-ok' : ''}`}>{money(t.amountCents, { sign: true })}</td>
                              <td>{categoryLabel(t.category)}</td>
                              <td className="fin-muted">{t.categorySource || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {txns.data.total > txns.data.rows.length && (
                        <div className="fin-muted" style={{ padding: '6px 14px' }}>showing {txns.data.rows.length} of {txns.data.total}</div>
                      )}
                    </div>
                  )}
              </div>
            )}
          </>
        );
      }}
    </Card>
  );
}

// ─── The page ────────────────────────────────────────────────────────────────

// Pure: everything it shows comes in as props. Each slot is
// { data, error, loading }; an error carries the HTTP status (api.js).
export function FinanceView({
  overview = {}, vendors = {}, report = null, txns = null, classify = null, acting = null,
  selectedMonth = null, selectedVendor = null,
  onRefresh, onRetryOverview, onRetryVendors, onSelectMonth, onSelectVendor, onAlways, onJustThis, onFinding,
}) {
  if (isRestricted([overview, vendors, report, txns, classify, ...Object.values(acting || {})])) {
    return <FinanceRestricted />;
  }
  const o = overview.data || null;
  const latestKey = o?.latestMonthly?.key || null;
  const monthKey = selectedMonth || latestKey;
  const statement = monthKey && monthKey === latestKey
    ? { data: o.latestMonthly }
    : (report && report.key === monthKey ? report : null);

  return (
    <div className="fin">
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap', marginBottom: 0 }}>
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>Finance</h1>
          <p className="page-subtitle" style={{ margin: 0 }}>
            {o?.asOf ? `as of ${stampLabel(o.asOf)} · ` : ''}{BASIS}
          </p>
        </div>
        {onRefresh && <button className="tab-refresh" onClick={onRefresh}>{overview.loading || vendors.loading ? 'loading…' : 'refresh'}</button>}
      </div>

      <CardBoundary name="Headline" resetKey={o}>
        <HeadlineStrip slot={overview} onRetry={onRetryOverview} />
      </CardBoundary>

      <div className="analytics-cols">
        <CardBoundary name="MRR" resetKey={o}><MrrCard slot={overview} onRetry={onRetryOverview} /></CardBoundary>
        <CardBoundary name="Findings" resetKey={o}><FindingsCard slot={overview} onRetry={onRetryOverview} acting={acting} onAct={onFinding} /></CardBoundary>
      </div>

      <CardBoundary name="P&L" resetKey={o}>
        <PnlCard slot={overview} onRetry={onRetryOverview} selectedKey={monthKey} statement={statement} onSelectMonth={onSelectMonth} />
      </CardBoundary>

      <div className="analytics-cols">
        <CardBoundary name="Vendors" resetKey={vendors.data}>
          <VendorsCard
            slot={vendors} onRetry={onRetryVendors} classify={classify}
            onAlways={onAlways} onJustThis={onJustThis}
            selectedVendor={selectedVendor} onSelectVendor={onSelectVendor} txns={txns}
          />
        </CardBoundary>
        <CardBoundary name="Cash" resetKey={o}><CashCard slot={overview} onRetry={onRetryOverview} /></CardBoundary>
      </div>
    </div>
  );
}

// Loads into a slot, keeping what was there while it refreshes so a refresh
// never blanks a card.
function useSlot(loader) {
  const [slot, setSlot] = useState({ loading: true });
  const load = useCallback(() => {
    setSlot(s => ({ data: s.data, loading: true }));
    return loader()
      .then(data => { setSlot({ data }); return data; })
      .catch(error => { setSlot(s => ({ data: s.data, error })); return null; });
  }, [loader]);
  return [slot, load];
}

export default function Finance() {
  const [overview, loadOverview] = useSlot(fetchFinanceOverview);
  const [vendors, loadVendors] = useSlot(fetchFinanceVendors);
  const [month, setMonth] = useState(null);
  const [report, setReport] = useState(null);      // { key, data, error }
  const [vendorKey, setVendorKey] = useState(null);
  const [txns, setTxns] = useState(null);          // { key, data, error }
  const [classify, setClassify] = useState(null);  // { busy, note, error, errorFor }
  const [acting, setActing] = useState({});        // findingId → { busy, error }
  const monthRef = useRef(null);
  const vendorRef = useRef(null);

  useEffect(() => { loadOverview(); loadVendors(); }, [loadOverview, loadVendors]);

  const loadReport = useCallback((key) => {
    setReport({ key });
    return fetchFinanceReport('monthly', key)
      .then(data => { if (monthRef.current === key) setReport({ key, data }); })
      .catch(error => { if (monthRef.current === key) setReport({ key, error }); });
  }, []);

  const loadTxns = useCallback((key) => {
    setTxns(t => (t?.key === key ? { key, data: t.data } : { key }));
    return fetchFinanceTransactions({ vendorKey: key, limit: 50 })
      .then(data => { if (vendorRef.current === key) setTxns({ key, data }); })
      .catch(error => { if (vendorRef.current === key) setTxns({ key, error }); });
  }, []);

  const selectMonth = (key) => {
    monthRef.current = key;
    setMonth(key);
    // The latest month is already in the overview; any other is a fetch.
    if (key !== overview.data?.latestMonthly?.key) loadReport(key);
  };

  const selectVendor = (key) => {
    const next = key === vendorKey ? null : key;
    vendorRef.current = next;
    setVendorKey(next);
    if (next) loadTxns(next);
    else setTxns(null);
  };

  const refresh = () => {
    loadOverview();
    loadVendors();
    if (month && report?.key === month) loadReport(month);
    if (vendorKey) loadTxns(vendorKey);
  };

  // After a classification the queue, the vendor table and the P&L's
  // uncategorised opex all change.
  const afterClassify = () => {
    loadVendors();
    loadOverview();
    if (vendorKey) loadTxns(vendorKey);
  };

  const classifyWith = async (row, category, mode) => {
    const id = counterpartyId(row);
    setClassify({ busy: id });
    try {
      if (mode === 'always') {
        const out = await saveFinanceVendorRule(ruleForCounterparty(row, category));
        const n = out?.rowsRecategorised;
        setClassify({ note: `${row.counterpartyName} → ${categoryLabel(category)}: rule saved${n != null ? `, ${n} row${n === 1 ? '' : 's'} recategorised` : ''}` });
      } else {
        await setFinanceTransactionCategory(row.sampleTxnId, category);
        setClassify({ note: `One ${row.counterpartyName} row → ${categoryLabel(category)}` });
      }
      afterClassify();
    } catch (error) {
      setClassify({ error, errorFor: id });
    }
  };

  const onFinding = async (id, action, note) => {
    setActing(a => ({ ...a, [id]: { busy: action } }));
    try {
      await actOnFinanceFinding(id, { action, note: note || undefined });
      setActing(a => { const next = { ...a }; delete next[id]; return next; });
      loadOverview();
      return true;
    } catch (error) {
      setActing(a => ({ ...a, [id]: { error } }));
      return false;
    }
  };

  return (
    <FinanceView
      overview={overview} vendors={vendors} report={report} txns={txns} classify={classify} acting={acting}
      selectedMonth={month} selectedVendor={vendorKey}
      onRefresh={refresh} onRetryOverview={loadOverview} onRetryVendors={loadVendors}
      onSelectMonth={selectMonth} onSelectVendor={selectVendor}
      onAlways={(row, category) => classifyWith(row, category, 'always')}
      onJustThis={(row, category) => classifyWith(row, category, 'one')}
      onFinding={onFinding}
    />
  );
}
